import { useState, useRef, useEffect } from 'react';
import { collection, addDoc, onSnapshot } from 'firebase/firestore';
import { db } from '@/libs/firebase/config';
import { DEFAULT_LOCALE, SUPPORTED_LOCALES } from '@/config/i18n';

export const useStripeCheckout = () => {
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState('idle'); // 'idle' | 'creating' | 'waiting' | 'redirecting'
  const [error, setError] = useState(null);
  const unsubscribeRef = useRef(null);
  const isMountedRef = useRef(true);

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
    };
  }, []);

  // Safe state setters that check if component is still mounted
  const safeSetLoading = (value) => {
    if (isMountedRef.current) {
      setLoading(value);
    }
  };

  const safeSetProgress = (value) => {
    if (isMountedRef.current) {
      setProgress(value);
    }
  };

  const safeSetError = (value) => {
    if (isMountedRef.current) {
      setError(value);
    }
  };

  const startCheckout = async (userId, priceId = process.env.NEXT_PUBLIC_STRIPE_PRICE_ID_PREMIUM_24H_DECEMBER_PROMO, locale = DEFAULT_LOCALE) => {
    if (!db) {
      safeSetError('Database configuration error');
      return;
    }

    // Clean up any existing listener
    if (unsubscribeRef.current) {
      unsubscribeRef.current();
      unsubscribeRef.current = null;
    }

    safeSetLoading(true);
    safeSetProgress('creating');
    safeSetError(null);

    try {
      const normalizedLocale = SUPPORTED_LOCALES.includes(locale.toLowerCase()) ? locale.toLowerCase() : DEFAULT_LOCALE;
      
      // Determine if this is a subscription or one-time payment
      const isSubscription = priceId === process.env.NEXT_PUBLIC_STRIPE_PRICE_ID_PREMIUM_MONTHLY ||
                             priceId === process.env.NEXT_PUBLIC_STRIPE_PRICE_ID_PREMIUM_QUARTERLY;
      
      const mode = isSubscription ? 'subscription' : 'payment';
      const productType = isSubscription 
        ? (priceId === process.env.NEXT_PUBLIC_STRIPE_PRICE_ID_PREMIUM_MONTHLY ? 'subscription_monthly' : 'subscription_quarterly')
        : 'premium_access_24h';
      
      // Step 1: Create checkout session document
      const checkoutSessionRef = collection(db, 'customers', userId, 'checkout_sessions');
      const docRef = await addDoc(checkoutSessionRef, {
        mode,
        price: priceId,
        success_url: `${window.location.origin}/${normalizedLocale}/temple/profile?success=1&priceId=${priceId}`,
        cancel_url: `${window.location.origin}/${normalizedLocale}/pricing?canceled=1`,
        metadata: {
          productType,
          priceId: priceId,
          firebaseUID: userId // Ensure webhook can find the user
        }
      });

      safeSetProgress('waiting');

      // Step 2: Listen for Stripe response
      const unsubscribe = onSnapshot(docRef, (snap) => {
        // Check if component is still mounted before updating state
        if (!isMountedRef.current) {
          unsubscribe();
          return;
        }

        const data = snap.data();
        const { error: stripeError, url } = data || {};

        if (stripeError) {
          console.error(`Stripe Checkout Error: ${stripeError.message}`);
          safeSetError(stripeError.message);
          safeSetLoading(false);
          safeSetProgress('idle');
          unsubscribe();
          unsubscribeRef.current = null;
          return;
        }

        if (url) {
          console.log('Redirecting to Stripe Checkout:', url);
          safeSetProgress('redirecting');
          
          // Small delay to show the redirecting state
          setTimeout(() => {
            if (isMountedRef.current) {
              window.location.assign(url);
            }
          }, 500);
          
          unsubscribe();
          unsubscribeRef.current = null;
        }
      }, (err) => {
        console.error('Firestore snapshot listener error:', err);
        if (isMountedRef.current) {
          safeSetError('Failed to listen for checkout session updates. Please try again.');
          safeSetLoading(false);
          safeSetProgress('idle');
        }
        unsubscribe();
        unsubscribeRef.current = null;
      });

      // Store unsubscribe function for cleanup
      unsubscribeRef.current = unsubscribe;

    } catch (error) {
      console.error('Error creating checkout session document:', error);
      safeSetError('Failed to initiate payment. Please try again.');
      safeSetLoading(false);
      safeSetProgress('idle');
    }
  };

  const reset = () => {
    // Clean up listener when resetting
    if (unsubscribeRef.current) {
      unsubscribeRef.current();
      unsubscribeRef.current = null;
    }
    safeSetLoading(false);
    safeSetProgress('idle');
    safeSetError(null);
  };

  return {
    loading,
    progress,
    error,
    startCheckout,
    reset
  };
}; 